import { z } from 'zod';
import { ApproximateDateSchema, approximateDateToDate, mesesDesde, type ApproximateDate } from './approximate-date.schema';

/**
 * @file clinical-profile.schema.ts
 * @description Base clínica da MULHER — o que não muda entre gestações.
 *
 * Antes isto morava dentro de `pregnancy.clinicalBaseline`, o que tinha duas
 * consequências: uma tentante não tinha onde guardar tipo sanguíneo ou alergia,
 * porque não existe documento de gestação para ela; e a mesma mulher com duas
 * gestações teria duas cópias, livres para divergir.
 *
 * ## O `id` é o id da MULHER
 *
 * Relação 1:1: o campo `id` deste perfil é o mesmo `id` da usuária. Não há
 * campo de ligação separado — a identidade É o vínculo. É a diferença para
 * `pregnancy.pregnantWomanId`, que é campo à parte numa relação 1:N e por isso
 * pôde divergir nas 57 gestações órfãs de produção.
 *
 * Seguindo a convenção do projeto, o id do DOCUMENTO continua autogerado e o
 * `id` vive como campo — igual a todas as outras coleções. Isso significa que
 * a unicidade 1:1 é **convenção garantida pelo `upsert`**, não estrutura: nada
 * no banco impede dois perfis com o mesmo `id`. Quem gravar por fora do
 * repositório pode criar duplicata.
 *
 * Um perfil sem dona continua inofensivo: nunca é lido sem a usuária, então não
 * aparece em listagem nem contamina contagem. E é detectável cruzando `id` com
 * `users`. A remoção acompanha a da usuária — que, pela política de soft
 * delete do time, na prática nunca some.
 */

export const BloodTypeEnum = z.enum([
  'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'UNKNOWN',
]);

/** Fator de risco marcado, no mesmo formato usado na gestação. */
export const ClinicalRiskFactorSchema = z.object({
  code: z.string(),
  label: z.string(),
  notes: z.string().nullable().optional(),
});

/** Como ela caracteriza o próprio ciclo. Bandeira, não série temporal. */
export const CycleRegularityEnum = z.enum(['REGULAR', 'IRREGULAR', 'UNKNOWN']);

/** Condutas na busca por engravidar, da mais simples à mais intervencionista. */
export const ConceptionModalityEnum = z.enum([
  'NATURAL',
  'TIMED_INTERCOURSE',
  'IUI',
  'IVF',
  'OTHER',
]);

/**
 * Um trecho da trajetória de tentativa. A progressão natural → coito programado
 * → IUI → FIV é linha do tempo dentro de um MESMO estado contínuo, não
 * episódios separados: o relógio da tentativa nunca zera ao trocar de conduta.
 *
 * Nasce vazia e cresce. Pedir o histórico no cadastro contradiria o "mínimo
 * necessário" — e reconstruir de memória produz dado ruim.
 */
export const ConceptionTimelineEntrySchema = z.object({
  id: z.string(),
  startedAt: ApproximateDateSchema,
  modality: ConceptionModalityEnum,
  /** Só em FIV. Contados em separado, por decisão clínica. */
  stimulationCycles: z.number().int().nonnegative().optional(),
  transfers: z.number().int().nonnegative().optional(),
  notes: z.string().nullable().optional(),
});

/**
 * O que a tentante registra. Vive no perfil clínico e não numa coleção
 * própria porque **tentar é estado contínuo, não episódio**: ela segue tentante
 * enquanto não engravidar, e perdas não interrompem.
 */
export const TryingToConceiveSchema = z.object({
  /**
   * Desde quando ela QUER engravidar. O relógio psicossocial — o vivido, que
   * inclui planejamento, esperas e perdas. **Nunca reinicia.**
   */
  wantingSince: ApproximateDateSchema.optional(),

  /**
   * Desde quando está sem método contraceptivo. Base do relógio clínico, que
   * conversa com o critério de infertilidade.
   *
   * **Não é o valor final:** infertilidade secundária conta a partir da última
   * gestação, então o relógio efetivo é o mais recente entre esta data e o fim
   * da última gestação. Ver `mesesSemMetodo`.
   */
  contraceptionStoppedAt: ApproximateDateSchema.optional(),

  cycleRegularity: CycleRegularityEnum.optional(),

  timeline: z.array(ConceptionTimelineEntrySchema).default([]),
});

/**
 * Etapas do processo de adoção no Brasil, na ordem em que acontecem.
 *
 * Adoção é PROCESSO, não estado — por isso é linha do tempo, como o tratamento
 * da tentante, e não uma bandeira.
 */
export const AdoptionStageEnum = z.enum([
  'HABILITACAO',   // inscrição e avaliação no Judiciário
  'ESPERA',        // habilitada, aguardando vinculação
  'CONVIVENCIA',   // estágio de convivência com a criança
  'SENTENCA',      // adoção concluída judicialmente
]);

export const AdoptionTimelineEntrySchema = z.object({
  id: z.string(),
  startedAt: ApproximateDateSchema,
  stage: AdoptionStageEnum,
  notes: z.string().nullable().optional(),
});

/**
 * O que a adotante registra.
 *
 * **A Eva não registra a criança adotada** — decisão do time. O acompanhamento
 * é da mulher, majoritariamente psicológico; a parte clínica é a preventiva de
 * qualquer mulher, que já vive no perfil.
 *
 * Ela deixa de ser adotante quando troca o próprio perfil para "mãe" no app.
 * Isso já funciona sem código novo: na derivação da fase, o `userType` estático
 * ganha do estado da gestação.
 */
export const AdoptingSchema = z.object({
  timeline: z.array(AdoptionTimelineEntrySchema).default([]),
});

/**
 * Em que momento da gestação a perda aconteceu, **como a mulher relata**.
 *
 * Diferente de `pregnancy.lossCarePoint`, que é preenchido por profissional
 * e segue faixas por trimestre. Estas são as faixas que o time clínico mapeou
 * para os checkpoints da jornada Perdi meu bebê (reunião de 02/10/2026).
 */
export const MomentoDaPerdaEnum = z.enum([
  'ANTES_DE_12_SEMANAS',
  'ENTRE_12_E_20_SEMANAS',
  'APOS_20_SEMANAS',
  'DURANTE_O_NASCIMENTO',
  'APOS_O_NASCIMENTO',
]);
export type MomentoDaPerda = z.infer<typeof MomentoDaPerdaEnum>;

/**
 * O que quem perdeu o bebê conta sobre a perda, no onboarding (A062, fase C).
 *
 * **Tudo opcional, sempre com "Prefiro não registrar".** Até 02/10/2026 a
 * regra era nunca perguntar à mulher sobre a perda (ver `lossCarePoint`). A
 * reunião de sprint de 02/10 decidiu perguntar no onboarding, porque o
 * momento da perda muda os checkpoints que fazem sentido — mas sem exigir
 * nada: quem não quiser falar segue igual.
 */
export const PerdaGestacionalSchema = z.object({
  momento: MomentoDaPerdaEnum.nullable().optional(),
  quando: z.coerce.date().nullable().optional(),
  nomeDoBebe: z.string().trim().max(120).nullable().optional(),
});
export type PerdaGestacional = z.infer<typeof PerdaGestacionalSchema>;

export const ClinicalProfileSchema = z.object({
  /** Igual ao campo `id` da usuária. É a identidade e o vínculo ao mesmo tempo. */
  id: z.string(),

  // --- Permanentes da mulher ---
  bloodType: BloodTypeEnum.nullable().optional(),
  heightCm: z.number().positive().optional(),
  allergies: z.array(z.string()).default([]),
  comorbidities: z.array(z.string()).default([]),

  /**
   * Só fatores de escopo WOMAN — ver `risk-factor.schema.ts`. Os de escopo
   * PREGNANCY continuam em `pregnancy.clinicalBaseline.riskFactors`, e os
   * DERIVED (idade) não são gravados em lugar nenhum.
   */
  riskFactors: z.array(ClinicalRiskFactorSchema).default([]),

  /** Preenchido para quem está tentando engravidar. */
  tryingToConceive: TryingToConceiveSchema.optional(),

  /** Preenchido para quem está em processo de adoção. */
  adopting: AdoptingSchema.optional(),

  /** Preenchido por quem perdeu o bebê, se ela quiser (A062). */
  perdaGestacional: PerdaGestacionalSchema.optional(),

  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type ClinicalProfile = z.infer<typeof ClinicalProfileSchema>;
export type TryingToConceive = z.infer<typeof TryingToConceiveSchema>;
export type ConceptionModality = z.infer<typeof ConceptionModalityEnum>;
export type CycleRegularity = z.infer<typeof CycleRegularityEnum>;
export type Adopting = z.infer<typeof AdoptingSchema>;
export type AdoptionStage = z.infer<typeof AdoptionStageEnum>;

/**
 * Meses do relógio CLÍNICO — tempo sem método contraceptivo.
 *
 * Reinicia a cada gestação: infertilidade secundária conta a partir da última.
 * Então o marco é o mais recente entre o fim do método e o fim da última
 * gestação, e por isso o valor é derivado, não gravado.
 */
export function mesesSemMetodo(
  trying: { contraceptionStoppedAt?: ApproximateDate } | null | undefined,
  fimDaUltimaGestacao?: Date | null,
  agora: Date = new Date(),
): number | null {
  const semMetodo = approximateDateToDate(trying?.contraceptionStoppedAt);
  if (!semMetodo) return null;

  const marco =
    fimDaUltimaGestacao && fimDaUltimaGestacao > semMetodo ? fimDaUltimaGestacao : semMetodo;

  return mesesDesde({ type: 'exact', date: marco }, agora);
}

/** Meses do relógio PSICOSSOCIAL — desde quando ela quer. Nunca reinicia. */
export function mesesTentando(
  trying: { wantingSince?: ApproximateDate } | null | undefined,
  agora: Date = new Date(),
): number | null {
  return mesesDesde(trying?.wantingSince, agora);
}
export type BloodType = z.infer<typeof BloodTypeEnum>;
export type ClinicalRiskFactor = z.infer<typeof ClinicalRiskFactorSchema>;

/** Campos que a fase expand ainda tolera ler de dentro da gestação. */
export interface LegacyClinicalBaselineFields {
  bloodType?: BloodType | null;
  heightCm?: number;
  allergies?: string[];
  comorbidities?: string[];
}

/**
 * Leitura tolerante às duas origens, para a fase expand do expand/contract.
 *
 * Enquanto o backfill não roda, um dado pode estar no perfil novo, na gestação
 * antiga, ou nos dois. O perfil ganha — é o destino, e quem escreveu lá o fez
 * depois. Só se ele não tiver o campo é que se olha o legado.
 */
export function mergeClinicalBaseline(
  profile: Partial<ClinicalProfile> | null | undefined,
  legado: LegacyClinicalBaselineFields | null | undefined,
): LegacyClinicalBaselineFields {
  return {
    bloodType: profile?.bloodType ?? legado?.bloodType ?? null,
    heightCm: profile?.heightCm ?? legado?.heightCm,
    allergies: profile?.allergies?.length ? profile.allergies : legado?.allergies ?? [],
    comorbidities: profile?.comorbidities?.length
      ? profile.comorbidities
      : legado?.comorbidities ?? [],
  };
}
